// Short-lived navigation data only. Authentication/privilege changes clear it;
// pending responses from an earlier generation cannot repopulate the cache.
const MAX_AGE_MS = 60_000;
const MAX_ENTRIES = 100;
const entries = new Map<string, { value: unknown; expiresAt: number }>();
const pending = new Map<string, Promise<unknown>>();
let generation = 0;

export const racerDataCache = {
  get generation() { return generation; },
  get<T>(key: string): T | undefined {
    const entry = entries.get(key);
    if (!entry || entry.expiresAt <= Date.now()) {
      entries.delete(key);
      return undefined;
    }
    return entry.value as T;
  },
  set<T>(key: string, value: T): T {
    entries.delete(key);
    entries.set(key, { value, expiresAt: Date.now() + MAX_AGE_MS });
    if (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
    return value;
  },
  load<T>(key: string, loader: () => Promise<T>): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== undefined) return Promise.resolve(cached);
    const existing = pending.get(key);
    if (existing) return existing as Promise<T>;
    const startedGeneration = generation;
    const request = Promise.resolve().then(loader).then(value => {
      if (generation === startedGeneration && pending.get(key) === request) this.set(key, value);
      return value;
    }).finally(() => {
      if (pending.get(key) === request) pending.delete(key);
    });
    pending.set(key, request);
    return request;
  },
  clear() {
    generation += 1;
    entries.clear();
    pending.clear();
  },
};
